import React, { useState, useEffect, useRef } from 'react';
import { 
  Users, 
  DollarSign, 
  Clock, 
  TrendingUp, 
  Download, 
  Search, 
  Filter,
  Eye,
  CheckCircle,
  Send,
  RefreshCw,
  Trash2,
  UserX,
  BarChart3,
  Building,
  CreditCard,
  Percent,
  Settings,
  Key
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { 
  getRegistryStats, 
  getAdminWithdrawalRequests, 
  getDashboardStats,
  getUserStats,
  exportRegistryData,
  exportWithdrawalData,
  exportUserData,
  getFeeSettings
} from '../lib/admin';
import VendorManagement from '../components/admin/VendorManagement';
import VenueManagement from '../components/admin/VenueManagement';
import AdminWithdrawalModal from '../components/AdminWithdrawalModal';
import DeleteRegistryModal from '../components/DeleteRegistryModal';
import DeleteUserModal from '../components/DeleteUserModal';
import FeeSettingsModal from '../components/FeeSettingsModal';
import ResetUserPasswordModal from '../components/ResetUserPasswordModal';
import type { RegistryStats, AdminWithdrawalRequest, AdminDashboardStats, UserStats } from '../types/admin';

export default function AdminDashboard() {
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [registryStats, setRegistryStats] = useState<RegistryStats[]>([]);
  const [userStats, setUserStats] = useState<UserStats[]>([]);
  const [withdrawalRequests, setWithdrawalRequests] = useState<AdminWithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'registries' | 'users' | 'withdrawals' | 'vendors' | 'venues' | 'fees'>('registries');
  const [selectedRequest, setSelectedRequest] = useState<AdminWithdrawalRequest | null>(null);
  const [selectedRegistry, setSelectedRegistry] = useState<RegistryStats | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserStats | null>(null);
  const [selectedUserForReset, setSelectedUserForReset] = useState<UserStats | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showFeeSettingsModal, setShowFeeSettingsModal] = useState(false);
  const [platformFeeRate, setPlatformFeeRate] = useState(0.0131); // Default 1.31%
  const [gatewayFeeRate, setGatewayFeeRate] = useState(0.0269); // Default 2.69%
  const sparklesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadData();
    
    const createSparkle = () => {
      if (!sparklesRef.current) return;
      
      const sparkle = document.createElement('div');
      sparkle.className = 'sparkle';
      
      const startX = Math.random() * window.innerWidth;
      const endX = startX + (Math.random() * 100 - 50);
      
      sparkle.style.setProperty('--start-x', `${startX}px`);
      sparkle.style.setProperty('--end-x', `${endX}px`);
      sparkle.style.left = `${startX}px`;
      
      sparklesRef.current.appendChild(sparkle);
      
      setTimeout(() => sparkle.remove(), 3000);
    };

    const interval = setInterval(createSparkle, 200);

    for (let i = 0; i < 10; i++) {
      setTimeout(createSparkle, i * 100);
    }

    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [dashboardStats, registries, users, withdrawals, feeSettings] = await Promise.all([
        getDashboardStats(),
        getRegistryStats(),
        getUserStats(),
        getAdminWithdrawalRequests(),
        getFeeSettings()
      ]);

      setStats(dashboardStats);
      setRegistryStats(registries);
      setUserStats(users);
      setWithdrawalRequests(withdrawals);
      setPlatformFeeRate(feeSettings.platformFeeRate);
      setGatewayFeeRate(feeSettings.gatewayFeeRate);
    } catch (err) {
      console.error('Error loading admin data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const filteredRegistries = registryStats.filter(registry => {
    const matchesSearch = 
      registry.owner_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      registry.owner_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      registry.title.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  const filteredUsers = userStats.filter(user => {
    const matchesSearch = 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  const filteredWithdrawals = withdrawalRequests.filter(request => {
    const matchesSearch = 
      request.owner_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.owner_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.registry_title.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-900/50 text-yellow-200';
      case 'approved':
        return 'bg-blue-900/50 text-blue-200';
      case 'transferred':
        return 'bg-green-900/50 text-green-200';
      default:
        return 'bg-gray-900/50 text-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'approved':
        return <CheckCircle className="w-4 h-4" />;
      case 'transferred':
        return <Send className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const handleExport = () => {
    switch (activeTab) {
      case 'registries':
        exportRegistryData();
        break;
      case 'users':
        exportUserData();
        break;
      case 'withdrawals':
        exportWithdrawalData();
        break;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg mb-4">{error}</p>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
      {/* Sparkles Container */}
      <div ref={sparklesRef} className="sparkles-container" />

      <div className="container mx-auto px-4 py-8">
        <div className="space-y-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
              <p className="text-gray-300 mt-2">Manage users, gift registries and withdrawal requests</p>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/admin/analytics"
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <BarChart3 className="w-4 h-4" />
                <span>Analytics Dashboard</span>
              </Link>
              <button
                onClick={() => setShowFeeSettingsModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                <Settings className="w-4 h-4" />
                <span>Fee Settings</span>
              </button>
              <button
                onClick={loadData}
                className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
              <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-6">
                <div className="flex items-center space-x-2 mb-2">
                  <Users className="w-5 h-5 text-blue-400" />
                  <span className="text-sm text-gray-400">Total Registries</span>
                </div>
                <p className="text-2xl font-bold text-white">{stats.total_registries}</p>
              </div>

              <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-6">
                <div className="flex items-center space-x-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                  <span className="text-sm text-gray-400">Total Collected</span>
                </div>
                <p className="text-2xl font-bold text-white">LKR {stats.total_collected.toFixed(2)}</p>
              </div>

              <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-6">
                <div className="flex items-center space-x-2 mb-2">
                  <Clock className="w-5 h-5 text-yellow-400" />
                  <span className="text-sm text-gray-400">Pending Requests</span>
                </div>
                <p className="text-2xl font-bold text-white">{stats.pending_withdrawals}</p>
              </div>

              <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-6">
                <div className="flex items-center space-x-2 mb-2">
                  <DollarSign className="w-5 h-5 text-yellow-400" />
                  <span className="text-sm text-gray-400">Pending Amount</span>
                </div>
                <p className="text-2xl font-bold text-white">LKR {stats.pending_withdrawal_amount.toFixed(2)}</p>
              </div>

              <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-6">
                <div className="flex items-center space-x-2 mb-2">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  <span className="text-sm text-gray-400">Total Withdrawn</span>
                </div>
                <p className="text-2xl font-bold text-white">LKR {stats.total_withdrawn.toFixed(2)}</p>
              </div>

              <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-6">
                <div className="flex items-center space-x-2 mb-2">
                  <Users className="w-5 h-5 text-purple-400" />
                  <span className="text-sm text-gray-400">Active Users</span>
                </div>
                <p className="text-2xl font-bold text-white">{stats.active_users}</p>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex flex-wrap space-x-1 bg-gray-800/50 p-1 rounded-lg">
            <button
              onClick={() => setActiveTab('registries')}
              className={`px-4 py-2 rounded-md transition-colors ${
                activeTab === 'registries'
                  ? 'bg-primary text-white'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Gift Registries
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`px-4 py-2 rounded-md transition-colors ${
                activeTab === 'users'
                  ? 'bg-primary text-white'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              User Accounts
            </button>
            <button
              onClick={() => setActiveTab('withdrawals')}
              className={`px-4 py-2 rounded-md transition-colors ${
                activeTab === 'withdrawals'
                  ? 'bg-primary text-white'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Withdrawal Requests
            </button>
            <button
              onClick={() => setActiveTab('vendors')}
              className={`px-4 py-2 rounded-md transition-colors ${
                activeTab === 'vendors'
                  ? 'bg-primary text-white'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Vendors
            </button>
            <button
              onClick={() => setActiveTab('venues')}
              className={`px-4 py-2 rounded-md transition-colors ${
                activeTab === 'venues'
                  ? 'bg-primary text-white'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Venues
            </button>
            <button
              onClick={() => setActiveTab('fees')}
              className={`px-4 py-2 rounded-md transition-colors ${
                activeTab === 'fees'
                  ? 'bg-primary text-white'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Fee Structure
            </button>
          </div>

          {/* Search and Filters */}
          {!['fees', 'vendors', 'venues'].includes(activeTab) && (
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={`Search ${activeTab === 'users' ? 'users' : activeTab === 'registries' ? 'registries' : 'requests'} by name, email, or title...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full px-3 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                />
              </div>

              {activeTab === 'withdrawals' && (
                <div className="flex items-center space-x-2">
                  <Filter className="w-5 h-5 text-gray-400" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="transferred">Transferred</option>
                  </select>
                </div>
              )}

              <button
                onClick={handleExport}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600"
              >
                <Download className="w-4 h-4" />
                <span>Export CSV</span>
              </button>
            </div>
          )}

          {/* Content */}
          {activeTab === 'registries' ? (
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-gray-900/50">
                    <tr>
                      <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Registry Details
                      </th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Owner Information
                      </th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Financial Summary
                      </th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Created Date
                      </th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {filteredRegistries.map((registry) => (
                      <tr key={registry.registry_id} className="hover:bg-gray-700/50">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-white">{registry.title}</p>
                            <p className="text-sm text-gray-400">ID: {registry.registry_id}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-white">{registry.owner_name}</p>
                            <p className="text-sm text-gray-400">{registry.owner_email}</p>
                            {registry.contact_number && (
                              <p className="text-sm text-gray-400">{registry.contact_number}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <p className="text-sm text-white">
                              Collected: <span className="font-medium">LKR {registry.total_collected.toFixed(2)}</span>
                            </p>
                            <p className="text-sm text-white">
                              Withdrawn: <span className="font-medium">LKR {registry.total_withdrawn.toFixed(2)}</span>
                            </p>
                            {registry.pending_requests_count > 0 && (
                              <p className="text-sm text-yellow-400">
                                Pending: {registry.pending_requests_count} (LKR {registry.pending_requests_amount.toFixed(2)})
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-400">
                          {new Date(registry.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <a
                              href={`/registries/${registry.registry_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                            >
                              <Eye className="w-4 h-4" />
                              <span>View</span>
                            </a>
                            <button
                              onClick={() => setSelectedRegistry(registry)}
                              className="flex items-center space-x-1 px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>Delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : activeTab === 'users' ? (
            <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-gray-900/50">
                    <tr>
                      <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                        User Details
                      </th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Account Info
                      </th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Registry Activity
                      </th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Financial Summary
                      </th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {filteredUsers.map((user) => (
                      <tr key={user.user_id} className="hover:bg-gray-700/50">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-white">{user.email}</p>
                            <p className="text-sm text-gray-400">{user.full_name || 'No name set'}</p>
                            <p className="text-sm text-gray-400">@{user.username || 'No username'}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <p className="text-sm text-white">
                              Joined: {new Date(user.created_at).toLocaleDateString()}
                            </p>
                            <p className="text-sm text-gray-400">
                              Last Sign In: {user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString() : 'Never'}
                            </p>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              user.is_active ? 'bg-green-900/50 text-green-200' : 'bg-red-900/50 text-red-200'
                            }`}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <p className="text-sm text-white">
                              Registries: <span className="font-medium">{user.registry_count}</span>
                            </p>
                            <p className="text-sm text-gray-400">
                              Pending Requests: {user.pending_requests_count}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <p className="text-sm text-white">
                              Collected: <span className="font-medium">LKR {user.total_collected.toFixed(2)}</span>
                            </p>
                            <p className="text-sm text-white">
                              Withdrawn: <span className="font-medium">LKR {user.total_withdrawn.toFixed(2)}</span>
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setSelectedUserForReset(user)}
                              className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                            >
                              <Key className="w-4 h-4" />
                              <span>Reset Password</span>
                            </button>
                            <button
                              onClick={() => setSelectedUser(user)}
                              className="flex items-center space-x-1 px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                            >
                              <UserX className="w-4 h-4" />
                              <span>Delete User</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : activeTab === 'withdrawals' ? (
            <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-gray-900/50">
                    <tr>
                      <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Request Details
                      </th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Registry & Owner
                      </th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Amount & Status
                      </th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {filteredWithdrawals.map((request) => (
                      <tr key={request.request_id} className="hover:bg-gray-700/50">
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-sm text-gray-400">Request ID</p>
                            <p className="font-mono text-sm text-white">{request.request_id}</p>
                            <p className="text-sm text-gray-400 mt-1">
                              {new Date(request.requested_at).toLocaleDateString()}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-white">{request.registry_title}</p>
                            <p className="text-sm text-gray-400">{request.owner_name}</p>
                            <p className="text-sm text-gray-400">{request.owner_email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-2">
                            <p className="font-medium text-white">LKR {request.amount.toFixed(2)}</p>
                            <div className="flex items-center space-x-2">
                              {getStatusIcon(request.status)}
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                                {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => setSelectedRequest(request)}
                            className="flex items-center space-x-1 px-3 py-1 bg-primary text-white rounded-md hover:bg-primary-600 text-sm"
                          >
                            <Eye className="w-4 h-4" />
                            <span>View</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : activeTab === 'vendors' ? (
            <VendorManagement />
          ) : activeTab === 'venues' ? (
            <VenueManagement />
          ) : (
            // Fee Structure Tab
            <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-6 space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-white mb-4">Transaction Fee Structure</h2>
                <button
                  onClick={() => setShowFeeSettingsModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600"
                >
                  <Settings className="w-4 h-4" />
                  <span>Edit Fee Settings</span>
                </button>
              </div>
              
              {/* Fee Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-blue-900/30 border border-blue-800 p-6 rounded-lg">
                  <div className="flex items-center space-x-3 mb-4">
                    <Building className="w-6 h-6 text-blue-400" />
                    <h3 className="text-lg font-medium text-blue-200">Platform Fee</h3>
                  </div>
                  <p className="text-3xl font-bold text-blue-300 mb-2">{platformFeeRate.toFixed(2)}%</p>
                  <p className="text-blue-200">Our service fee for providing the gift registry platform</p>
                  <div className="mt-4 pt-4 border-t border-blue-800">
                    <p className="text-blue-300 font-medium">Revenue Calculation</p>
                    <p className="text-blue-200 text-sm mt-1">
                      For a LKR 10,000 transaction:<br />
                      LKR 10,000 × {platformFeeRate.toFixed(2)}% = LKR {(10000 * (platformFeeRate / 100)).toFixed(2)}
                    </p>
                  </div>
                </div>
                
                <div className="bg-purple-900/30 border border-purple-800 p-6 rounded-lg">
                  <div className="flex items-center space-x-3 mb-4">
                    <CreditCard className="w-6 h-6 text-purple-400" />
                    <h3 className="text-lg font-medium text-purple-200">Gateway Fee</h3>
                  </div>
                  <p className="text-3xl font-bold text-purple-300 mb-2">{gatewayFeeRate.toFixed(2)}%</p>
                  <p className="text-purple-200">Payment processor fee for handling transactions</p>
                  <div className="mt-4 pt-4 border-t border-purple-800">
                    <p className="text-purple-300 font-medium">Cost Calculation</p>
                    <p className="text-purple-200 text-sm mt-1">
                      For a LKR 10,000 transaction:<br />
                      LKR 10,000 × {gatewayFeeRate.toFixed(2)}% = LKR {(10000 * (gatewayFeeRate / 100)).toFixed(2)}
                    </p>
                  </div>
                </div>
                
                <div className="bg-green-900/30 border border-green-800 p-6 rounded-lg">
                  <div className="flex items-center space-x-3 mb-4">
                    <Percent className="w-6 h-6 text-green-400" />
                    <h3 className="text-lg font-medium text-green-200">Total Fee</h3>
                  </div>
                  <p className="text-3xl font-bold text-green-300 mb-2">{(platformFeeRate + gatewayFeeRate).toFixed(2)}%</p>
                  <p className="text-green-200">Combined fee charged on each transaction</p>
                  <div className="mt-4 pt-4 border-t border-green-800">
                    <p className="text-green-300 font-medium">Total Fee Calculation</p>
                    <p className="text-green-200 text-sm mt-1">
                      For a LKR 10,000 transaction:<br />
                      LKR 10,000 × {(platformFeeRate + gatewayFeeRate).toFixed(2)}% = LKR {(10000 * ((platformFeeRate + gatewayFeeRate) / 100)).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Example Transaction */}
              <div className="bg-gray-900/50 p-6 rounded-lg mt-8">
                <h3 className="text-lg font-semibold text-white mb-4">Example Transaction Breakdown</h3>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-white font-medium mb-3">Customer Perspective</h4>
                      <div className="space-y-2 bg-gray-800/50 p-4 rounded-lg">
                        <div className="flex justify-between">
                          <span className="text-gray-300">Original Amount:</span>
                          <span className="text-white font-medium">LKR 10,000.00</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Platform Fee ({platformFeeRate.toFixed(2)}%):</span>
                          <span className="text-white font-medium">LKR {(10000 * (platformFeeRate / 100)).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Gateway Fee ({gatewayFeeRate.toFixed(2)}%):</span>
                          <span className="text-white font-medium">LKR {(10000 * (gatewayFeeRate / 100)).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-gray-700">
                          <span className="text-white font-medium">Total Paid:</span>
                          <span className="text-white font-bold">LKR {(10000 * (1 + (platformFeeRate + gatewayFeeRate) / 100)).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-white font-medium mb-3">Admin Perspective</h4>
                      <div className="space-y-2 bg-gray-800/50 p-4 rounded-lg">
                        <div className="flex justify-between">
                          <span className="text-gray-300">Transfer to Client:</span>
                          <span className="text-green-400 font-medium">LKR 10,000.00</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Our Revenue ({platformFeeRate.toFixed(2)}%):</span>
                          <span className="text-blue-400 font-medium">LKR {(10000 * (platformFeeRate / 100)).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Gateway Cost ({gatewayFeeRate.toFixed(2)}%):</span>
                          <span className="text-purple-400 font-medium">LKR {(10000 * (gatewayFeeRate / 100)).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-gray-700">
                          <span className="text-white font-medium">Total Transaction:</span>
                          <span className="text-white font-bold">LKR {(10000 * (1 + (platformFeeRate + gatewayFeeRate) / 100)).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-blue-900/30 border border-blue-800 p-4 rounded-lg mt-4">
                    <h4 className="text-blue-200 font-medium mb-2">Net Profit Calculation</h4>
                    <div className="space-y-1">
                      <p className="text-blue-300">
                        <span className="font-medium">Platform Revenue:</span> LKR {(10000 * (platformFeeRate / 100)).toFixed(2)}
                      </p>
                      <p className="text-blue-300">
                        <span className="font-medium">Gateway Costs:</span> LKR {(10000 * (gatewayFeeRate / 100)).toFixed(2)}
                      </p>
                      <p className="text-blue-300">
                        <span className="font-medium">Net Profit:</span> LKR {(10000 * (platformFeeRate / 100)).toFixed(2)} (Platform fee is our revenue)
                      </p>
                      <p className="text-blue-300">
                        <span className="font-medium">Profit Margin:</span> {(platformFeeRate / (platformFeeRate + gatewayFeeRate) * 100).toFixed(2)}% of total fees
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Fee Policy */}
              <div className="bg-gray-900/50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-white mb-4">Fee Policy</h3>
                <div className="space-y-4">
                  <p className="text-gray-300">
                    Our platform charges a total fee of {(platformFeeRate + gatewayFeeRate).toFixed(2)}% on all transactions, which is broken down as follows:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300">
                    <li><span className="text-blue-300 font-medium">{platformFeeRate.toFixed(2)}% Platform Fee</span> - This covers our operational costs and platform maintenance</li>
                    <li><span className="text-purple-300 font-medium">{gatewayFeeRate.toFixed(2)}% Payment Gateway Fee</span> - This is charged by our payment processor</li>
                  </ul>
                  <p className="text-gray-300">
                    The original gift amount is transferred in full to the registry owner. The fees are added on top of the gift amount and paid by the gift giver.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Withdrawal Request Modal */}
      {selectedRequest && (
        <AdminWithdrawalModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onUpdate={loadData}
        />
      )}

      {/* Delete Registry Modal */}
      {selectedRegistry && (
        <DeleteRegistryModal
          registry={selectedRegistry}
          onClose={() => setSelectedRegistry(null)}
          onDelete={loadData}
        />
      )}

      {/* Delete User Modal */}
      {selectedUser && (
        <DeleteUserModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onDelete={loadData}
        />
      )}

      {/* Reset User Password Modal */}
      {selectedUserForReset && (
        <ResetUserPasswordModal
          user={selectedUserForReset}
          onClose={() => setSelectedUserForReset(null)}
          onSuccess={loadData}
        />
      )}

      {/* Fee Settings Modal */}
      {showFeeSettingsModal && (
        <FeeSettingsModal
          onClose={() => setShowFeeSettingsModal(false)}
          onUpdate={loadData}
        />
      )}
    </div>
  );
}